/**
 * Docs CMS — admin-only Type-3 surface at /admin/docs (Day 100; READ-ONLY as of
 * Day 100b.1). Help docs are authored as git-tracked markdown (docs/help/) and
 * synced into public.docs_pages by the CI loader (scripts/docs-loader.cjs) on
 * deploy — git is the sole author. This surface is now a read-only preview-over-
 * git: a list of the synced rows (admin sees drafts + published via the is_admin()
 * arm of docs_pages_select_published) + the selected row rendered through the
 * shared <MarkdownView> (identical to what arbi-docs ships — the RBP:1387 round-
 * trip). No editor, no publish toggle, no slug generation — those write paths
 * retired with the git loader.
 *
 * ReleaseNotesManager stays CMS-authored (release_notes is not git-sourced).
 */

import { useState, useEffect, useCallback, type JSX } from 'react';
import { FileText } from 'lucide-react';
import clsx from 'clsx';
import { supabase } from '../lib/supabase';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import MarkdownView from '../components/ui/MarkdownView';
import type { Database } from '../../../../src/lib/database.types';

type DocsPage = Database['public']['Tables']['docs_pages']['Row'];

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function DocsCMS(): JSX.Element {
  useDocumentTitle('Docs CMS');

  const [rows, setRows] = useState<DocsPage[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const loadRows = useCallback(async (): Promise<void> => {
    setLoading(true);
    const { data, error } = await supabase
      .from('docs_pages')
      .select('*')
      .order('updated_at', { ascending: false });
    if (error) {
      setLoadError(`Failed to load docs — ${error.message}`);
      setLoading(false);
      return;
    }
    const list = data ?? [];
    setRows(list);
    setSelectedId((prev) => prev ?? list[0]?.id ?? null);
    setLoading(false);
  }, []);

  useEffect(() => {
    void loadRows();
  }, [loadRows]);

  const selected = rows.find((r) => r.id === selectedId) ?? null;
  const publishedCount = rows.filter((r) => r.is_published).length;

  return (
    <div className="h-full overflow-auto">
      <div className="max-w-5xl mx-auto p-5 space-y-4">
        {/* Header */}
        <div>
          <h1 className="text-base font-semibold text-ink-bright">Docs CMS</h1>
          <p className="text-xs text-ink-muted">
            {rows.length === 1 ? '1 doc' : `${rows.length} docs`}
            {rows.length > 0 ? ` · ${publishedCount} published` : ''} · read-only preview
          </p>
          <p className="text-2xs text-ink-muted mt-1">
            Help docs are authored in git (<span className="font-mono">docs/help/</span>) and synced
            on deploy — this is a read-only preview.
          </p>
        </div>

        {loadError && <p className="text-xs text-danger">{loadError}</p>}

        {loading ? (
          <p className="text-xs text-ink-muted py-4">Loading…</p>
        ) : rows.length === 0 ? (
          <div className="arbi-card p-8 text-center">
            <FileText size={20} className="mx-auto text-ink-muted mb-2" />
            <p className="text-sm text-ink-secondary">No docs yet</p>
            <p className="text-xs text-ink-muted mt-1">
              Add a help article under <span className="font-mono">docs/help/</span> in git — it
              appears here once the loader syncs it.
            </p>
          </div>
        ) : (
          <div className="flex gap-4 min-h-0">
            {/* Row list (read-only) */}
            <div className="w-72 flex-shrink-0 space-y-2">
              {rows.map((row) => (
                <button
                  key={row.id}
                  type="button"
                  onClick={() => setSelectedId(row.id)}
                  className={clsx(
                    'w-full text-left py-2.5 px-3 rounded-lg transition-colors',
                    row.id === selectedId ? 'bg-accent/15' : 'bg-surface-2 hover:bg-surface-3'
                  )}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-ink-primary truncate">
                      {row.title}
                    </span>
                    <span
                      className={clsx(
                        'px-1.5 py-0.5 rounded text-2xs font-medium flex-shrink-0',
                        row.is_published
                          ? 'bg-success/15 text-success'
                          : 'bg-surface-4 text-ink-muted'
                      )}
                    >
                      {row.is_published ? 'Published' : 'Draft'}
                    </span>
                  </div>
                  <p className="font-mono text-2xs text-ink-muted mt-0.5 truncate">{row.slug}</p>
                  <p className="text-2xs text-ink-muted">Updated {fmtDate(row.updated_at)}</p>
                </button>
              ))}
            </div>

            {/* Read-only rendered preview */}
            <div className="flex-1 min-w-0 arbi-card p-5 overflow-auto">
              {selected ? (
                <MarkdownView
                  markdown={selected.body}
                  onInternalLink={(s) => {
                    const row = rows.find((r) => r.slug === s);
                    if (row) setSelectedId(row.id);
                  }}
                />
              ) : (
                <p className="text-sm text-ink-muted">Select a doc to preview.</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
